import { useEffect, useState, useContext, useRef } from "react";
import connect from "./api/connect";
import { useHeritageConnection } from "./hooks/useHeritageConnection";
import { useHeritageAuthStatus } from "../../../../../hooks/useHeritageAuthStatus";
import { set } from "lodash";
import { IBranch, IHeritageToken } from "./types/IHeritageConnection";
import { useQuery, useQueryClient } from "react-query";
import exp from "constants";
import axios from "axios";
import VendorBranches from "./components/Branches";
import { Button } from "components/buttonV2"
import { useAreYouSure } from "components/areYouSure";
import { VendorContext } from "contexts/VendorContext";


const VendorConnectionModal = ({setIsConnectionModalOpen, refetch: vendorRefetch}:any) => {

  const { data, isLoading, isError, refetch, error } = useHeritageConnection();
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);

  // New auth status hook (read-only for now)
  const { 
    data: authStatus, 
    isLoading: isAuthStatusLoading, 
    isError: isAuthStatusError 
  } = useHeritageAuthStatus();

  // Log the new status data for development (remove in production)
  useEffect(() => {
    if (authStatus) {
      console.log("[Heritage] New auth status:", authStatus);
    }
  }, [authStatus]);


  const [customerAccountId, setCustomerAccountId] = useState<string | null>(null);
  const [customerApiKey, setCustomerApiKey] = useState<string | null>(null);

  const [isHeritageModal, setIsHeritageModal] = useState(false);
  const [isSiteoneModal, setIsSiteoneModal] = useState(false);
  const areYouSure = useAreYouSure();

  const { currentVendor, updater, updateMode, toggleUpdateModeButton } =
  useContext(VendorContext);

  // const hasTriggeredRef = useRef(false);

  // useEffect(() => {
  //   if (isSiteoneModal && currentVendor && !hasTriggeredRef.current) {
  //     hasTriggeredRef.current = true;

  //     updateVendorSiteoneStatus({
  //       target: { checked: !currentVendor?.is_siteone }
  //     });
  //   }

  //   // Reset so it can trigger again next time modal opens
  //   if (!isSiteoneModal) {
  //     hasTriggeredRef.current = false;
  //   }
  // }, [isSiteoneModal, currentVendor]);



  if (isLoading) return <p>Loading connection details...</p>;
  if (isError) return <p>Failed to fetch connection details.</p>;



  const credentialsExist = data?.credentials_exist;

  const handleConnect = async () => {
    setFeedback(null);
    try {
 
      await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/connect`);
      setFeedback("Connected to Heritage.");
      queryClient.invalidateQueries("heritage-connection");
    } catch (error) {
      // setFeedback("Failed to connect to Heritage.");
      console.error(error);
    }
  };

  const handleDisconnect = async () => {
    setFeedback(null);
    try {
      await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/disconnect`);
      queryClient.invalidateQueries("heritage-connection");
      setFeedback("Disconnected from Heritage.");
    } catch (error) {
      setFeedback("Failed to disconnect from Heritage.");
      console.error(error);
    }
  };

  const handleSave = async () => {
    setFeedback(null);
    if (!customerAccountId || !customerApiKey) {
      setFeedback("Both fields are required.");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/update-credentials`, {
        customer_account_id: customerAccountId,
        customer_api_key: customerApiKey,
      });
      setFeedback("Company information saved successfully.");
      queryClient.invalidateQueries("heritage-connection");
    } catch (error) {
      setFeedback("Failed to save company information.");
      console.error(error);
    }
  };

  const handleRemoveCredentials = async () => {
    setFeedback(null);
    try {
      await axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/remove-credentials`);
      setCustomerAccountId(null);
      setCustomerApiKey(null);
      queryClient.invalidateQueries("heritage-connection");
      setFeedback("Credentials removed successfully.");
    } catch (error) {
      setFeedback("Failed to remove credentials.");
      console.error(error);
    }
  };

  const handleGetBranches = async () => {
    try {
      const branchesResponse = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-branches`);
    } catch (error) {
      console.error("Error fetching branches:", error);

    }
  };

  const createdAt = data?.created_at ? new Date(data.created_at) : null;
  const expiresAt = createdAt && data?.expires_in ? new Date(createdAt.getTime() + data.expires_in * 1000) : null;
  const expiresAtString = expiresAt ? expiresAt.toLocaleString() : "N/A";

  const getExpirationCountdown = (expiresAt: Date) => {
    const diffMs = expiresAt.getTime() - Date.now();
    if (diffMs <= 0) return "Expired";
    const mins = Math.floor(diffMs / 1000 / 60);
    return `${mins} min${mins !== 1 ? "s" : ""} remaining`;
  };

  const timeRemaining = expiresAt ? getExpirationCountdown(expiresAt) : "N/A";
  const isConnected = Boolean(expiresAt && Date.now() < expiresAt.getTime());

  const updateVendorSiteoneStatus = async (e: any) => {


    if (!currentVendor || currentVendor.vendor_id == null) return; // guard clause
  
    const updatedVendor: any = {
      ...currentVendor,
      is_siteone: e.target.checked,
      vendor_id: currentVendor.vendor_id, // ensures it's present and typed correctly
    };
  
    await updater(updatedVendor);
    await toggleUpdateModeButton(true, refetch, updatedVendor, true);


    await queryClient.invalidateQueries(['vendors-items', currentVendor.vendor_id]);

  };

  if (isHeritageModal) {
    return (
      <div className="p-4 bg-white shadow rounded overflow-y-auto max-h-[80vh]">
        <h2 className="text-lg font-semibold mb-2">Connection</h2>

        {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        {feedback && <p className="text-sm text-blue-600 mb-2">{feedback}</p>}

        {data && (
          <div className="text-sm text-gray-700 bg-gray-50 border rounded p-3 space-y-1">
            <div
              className="flex justify-between items-center"
            >
              <p><strong>Status:</strong></p><div className={`flex items-center gap-1 ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
                {isConnected ? "🟢 Connected" : "🔴 Not Connected"}
              </div>
            </div>
            {!isConnected && expiresAt ? (
              <p>
                <strong>Token expired at:</strong> {expiresAt.toLocaleString()}
              </p>
            ) : (
              <p><strong>Token Expires At:</strong> {expiresAtString}</p>)}
            <p><strong>Time Remaining:</strong> {timeRemaining}</p>
            {/* <p><strong>Token Type:</strong> {data.token_type}</p> */}
            <p><strong>Message:</strong> {data.message}</p>

          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          {!isConnected ? (
            <button
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
              onClick={handleConnect}
            >
              Connect
            </button>
          ) : (
            <button
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
              onClick={handleDisconnect}
            >
              Disconnect
            </button>
          )}
        </div>

        <hr className="my-4" />
        <h2 className="text-lg font-semibold mb-2">Credentials</h2>

        <div className="text-sm text-gray-700 bg-gray-50 border rounded p-3 mt-4 space-y-2">

        <div className="flex justify-between items-center">
          <p><strong>Status:</strong></p>
          {credentialsExist ? (
            <div className="flex justify-between items-center">
              <p className="text-green-600 text-sm">✅ Credentials Stored</p>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="password"
                className="w-full p-2 border rounded text-sm"
                placeholder="Enter Customer Account Id"
                value={customerAccountId ?? ""}
                onChange={(e) => setCustomerAccountId(e.target.value)}
              />
              <input
                type="password"
                className="w-full p-2 border rounded text-sm"
                placeholder="Enter Customer API Key"
                value={customerApiKey ?? ""}
                onChange={(e) => setCustomerApiKey(e.target.value)}
              />

              </div>
            )}
          </div>

        </div>

        <div className="flex justify-end gap-2 mt-4">

          {credentialsExist ? (
            <button
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
              onClick={handleRemoveCredentials}
            >
              Remove Account
            </button>
          ) : (
            <button
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
              onClick={handleSave}
              disabled={!customerAccountId || !customerApiKey}
            >
              Save
            </button>
          )}
        </div>

        <hr className="my-4" />
        <h2 className="text-lg font-semibold mb-2">Branches</h2>

        <VendorBranches />

        <div className="flex justify-end gap-2 mt-4">
          <button
            className="px-4 py-2 bg-white-500 hover:bg-white-600 text-gray rounded border"
            onClick={handleGetBranches}
          >
            Check for Branches
          </button>
        </div>

      </div >
    );
  }

  if (isSiteoneModal) {
    return (
      <div className="p-4 bg-white shadow rounded overflow-y-auto max-h-[80vh]">
        <h2 className="text-lg font-semibold mb-2">SiteOne Vendor</h2>

        <p className="mb-4">
          {currentVendor?.is_siteone
            ? "This vendor is currently marked as a SiteOne vendor."
            : "This vendor is not currently marked as a SiteOne vendor."}
        </p>

        <div className="flex justify-end gap-2">
          <Button
            className="bg-blue-500 hover:bg-blue-600 text-white"
            onClick={() => {
              updateVendorSiteoneStatus({
                target: { checked: !currentVendor?.is_siteone },
              })
              // close the modal
              setIsConnectionModalOpen(false);
            }
            }
          >
            {currentVendor?.is_siteone ? "Remove SiteOne Vendor" : "Make SiteOne Vendor"}
          </Button>

          <Button
            className="bg-gray-300 hover:bg-gray-400"
            onClick={() => setIsSiteoneModal(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    // create a button for heritage and for siteone
    <div className="p-4 bg-white shadow rounded overflow-y-auto max-h-[80vh]">
      <h2 className="text-lg font-semibold mb-2">Select Connection</h2>
      <div style={{ display: "flex", justifyContent: "space-between", width: "200px" }}>
        <Button
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
          onClick={() => setIsHeritageModal(true)}
          disabled={currentVendor?.is_siteone as any}
        >
          Heritage
        </Button>
        <Button
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
          onClick={() => setIsSiteoneModal(true)}
        >
          SiteOne
        </Button>
      </div>
    </div>
    
  );
};

export default VendorConnectionModal;
